import {IChatMessageThread, IChatMessageThreadData} from "@buildwithflux/models";
import {arrayToMapByUid, Records} from "@buildwithflux/shared";
import {entries} from "lodash";

import {
    CopilotChatStoreGetter,
    CopilotChatStoreSetter,
} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/useCopilotChatStore";

export function handleBackendUpdatedThreads(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    addedOrChangedThreads: IChatMessageThreadData[],
) {
    const updatedThreadsData = arrayToMapByUid(addedOrChangedThreads);

    // Capture whether we had any threads before this update.
    // We only want to auto-select a thread when we're receiving the first threads
    // for this document (initial load), not on subsequent updates (e.g., during
    // streaming), to avoid overriding the user's choice when they intentionally
    // navigate to a new thread.
    const hadThreadsBefore = !Records.isEmpty(get().threads);

    set((state) => {
        // Add blank messages to start with
        const updatedThreadsWithEmptyMessages = entries(updatedThreadsData).reduce(
            (
                acc: {
                    [threadUid: string]: IChatMessageThread;
                },
                [threadUid, threadData],
            ) => {
                acc[threadUid] = {
                    ...threadData,
                    messages: state.threads?.[threadUid]?.messages || {},
                };

                return acc;
            },
            {},
        );

        return {
            ...state,
            threadsLoaded: true,
            threads: {
                ...state.threads,
                ...updatedThreadsWithEmptyMessages,
            },
        };
    });

    const {currentThreadUid, pendingThreadUidFromUrl, threads} = get();

    // Only auto-select a thread on initial load (when we had no threads before).
    // This prevents overriding the user's choice when they intentionally navigate
    // to a new thread while another thread is streaming.
    if (!currentThreadUid && !hadThreadsBefore) {
        if (pendingThreadUidFromUrl) {
            if (threads[pendingThreadUidFromUrl]) {
                get().setCurrentThread(pendingThreadUidFromUrl);
                get().clearPendingThreadFromUrl();
            } else if (!Records.isEmpty(threads)) {
                get().setCurrentThreadToLatest();
                get().clearPendingThreadFromUrl();
            }
        } else {
            get().setCurrentThreadToLatest();
        }
    }

    // Update navbar title
    set(
        (draft) => {
            const currentThreadUid = draft.currentThreadUid;
            if (!currentThreadUid) {
                return;
            }
            const currentThread = draft.threads[currentThreadUid];
            draft.navbarTitle = currentThread?.name;
        },
        undefined,
        "update navbar title in handleBackendUpdatedThreads",
    );
}
